import { HttpErrorResponse } from '@angular/common/http';
import { Component, inject, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { ColumnStore, GridColumnConfig, MenuItemConfig, Menu, ColumnConfig } from '@bryntum/grid';
import { BryntumGridComponent, BryntumDateRangeFieldComponent, BryntumComboComponent, BryntumGridProps, BryntumGridModule } from '@bryntum/grid-angular';
import { NgbCalendar, NgbDate, NgbDatepickerModule, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngrx/store';
import dayjs from 'dayjs';
import { Subscription, Subject, take } from 'rxjs';
import writeXlsxFile, { Cell } from 'write-excel-file/browser';
import { getNumberColumnGenericConfig } from '../../../../../shared/bryntum/Helper/MogoBryntumGridConfigHelper';
import { selectUserType } from '../../../../../shared/app-configuration/app-configuration.reducer';
import { UserType } from '../../../../../shared/app-configuration/models/UserType';
import { BlobStorageService } from '../../../../../shared/blob-storage/blob-storage.service';
import { loadGridState, saveGridState } from '../../../../../shared/bryntum/bryntum-grid-state.actions';
import { selectGridStateById } from '../../../../../shared/bryntum/bryntum-grid-state.reducer';
import { ProofOfDeliveryPdfService } from '../../../../../shared/proof-of-delivery/proof-of-delivery-pdf.service';
import { IRootState } from '../../../../app.config';
import { JobbingService } from '../../../../../shared/Jobbing/jobbing.service';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { bootstrapArrowsAngleContract, bootstrapArrowsAngleExpand } from '@ng-icons/bootstrap-icons';
import { Time } from '@angular/common';
import { BillingUomEnum } from '../../../../../shared/Jobbing/enums/BillingUomEnum';
import { JobServicePromiseEnum } from '../../../../../shared/Jobbing/enums/JobServicePromiseEnum';
import { WorkOrderType } from '../../../../../shared/Jobbing/enums/WorkOrderType';
import { Job } from '../../../../../shared/Jobbing/models/Job';
import { JobProductBryntumModel } from '../../../../../shared/Jobbing/models/JobProductBryntumModel';
import { JobProductItem } from '../../../../../shared/Jobbing/models/JobProductItem';
import { JobServicePromise } from '../../../../../shared/Jobbing/models/JobServicePromise';
import { ToasterService } from '../../../../../shared/toaster/toaster.service';
import { JobbingJobPreviewPodModalComponent } from '../../../../../shared/modals/jobbing-job-preview-pod-modal/jobbing-job-preview-pod-modal.component';
import { JobbingJobEyeViewModalComponent } from '../../../../../shared/modals/jobbing-job-eye-view-modal/jobbing-job-eye-view-modal.component';
import { DateTimeHelper } from '../../../../../shared/DateTime/DateTimeHelper';

@Component({
  selector: 'app-jobbing-home-view',
  standalone: true,
  imports: [NgbDatepickerModule, BryntumGridModule, NgIcon],
  templateUrl: './jobbing-home-view.component.html',
  styleUrl: './jobbing-home-view.component.scss',
  viewProviders: [
    provideIcons({
      bootstrapArrowsAngleContract,
      bootstrapArrowsAngleExpand,
    })
  ]
})
export class JobbingHomeViewComponent {
  calendar = inject(NgbCalendar);

  data: any[] = [];
  totalItems = 0;
  gridHeight: string = '79vh';
  gridState: any | null = null;
  isInitializing = true;
  saveGridState$: Subscription | null = null;
  hoveredDate: NgbDate | null = null;
  lastGridSave: dayjs.Dayjs = dayjs();
  saveStateCounter: number = 0;
  ngDestroyed$ = new Subject();
  jobbingResults: any[] = [];

  private _jobbingGrid: BryntumGridComponent | null = null;
  @ViewChild('jobbingGrid')
  set jobbingGrid(value: BryntumGridComponent | null) {
    this._jobbingGrid = value;
    this.updateTotalItemsFromStore();
  }
  get jobbingGrid(): BryntumGridComponent | null {
    return this._jobbingGrid;
  }

  @ViewChild('dateFilterInput')
  dateFilterInput: BryntumDateRangeFieldComponent | null = null;

  @ViewChild('deliveryDateFilterInput')
  deliveryDateFilterInput: BryntumComboComponent | null = null;

  userType: UserType | null = null;

  defaultDaysToFilter: number = 7;

  constructor(private router: Router,
    private readonly store$: Store<IRootState>,
    private readonly modalService: NgbModal,
    private readonly proofOfDeliveryPdfService: ProofOfDeliveryPdfService,
    private readonly blobStorageService: BlobStorageService,
    private readonly jobbingService: JobbingService,
    private toasterService: ToasterService,
  ) { }

  gridProps: BryntumGridProps | null = null;

  dateRangeConfig: Partial<BryntumDateRangeFieldComponent> | any = {
    format: 'DD/MM/YYYY',
    value: [dayjs().subtract(this.defaultDaysToFilter, 'days'), 'today'],
    listeners: {
      change: (event: any) => {
        this.saveFilterState(event.value[0], event.value[1]);
        this.loadJobsList();
      },
      catchAll: (event: any) => {
        if (event.type === 'paint' && event.firstPaint) {
          const filterState = this.getFilterState();
          if (filterState) {
            this.dateRangeConfig.value = [dayjs(filterState.from), dayjs(filterState.to)];
            this.loadJobsList();
          }
        }
      }
    }
  }

  resetFilters() {
    this.dateRangeConfig.value = [dayjs().subtract(this.defaultDaysToFilter, 'days'), 'today'];
    this.loadJobsList();
  }

  ngOnInit() {
    this.isInitializing = true;
    this.store$.select(selectUserType).subscribe((userType) => {
      this.userType = userType;
      let columns: (GridColumnConfig & { field: string, excelFormat?: string | ((value: number) => string) })[] = [
        { id:'job_status', field: 'status', text: 'Status', autoWidth: 1 },
        { id:'job_orderNumber', field: 'orderNumber', text: 'Order Number', autoWidth: 1 },
        { id:'job_fromName', field: 'fromName', text: 'From', autoWidth: 1 },
        { id:'job_toName', field: 'toName', text: 'To', autoWidth: 1 },
        { id:'job_itemDescription', field: 'itemDescription', text: 'Item Description', autoWidth: 1 },
        { id:'job_quantity', field: 'quantity', text: 'Quantity', autoWidth: 1, ...getNumberColumnGenericConfig() },
        { id:'job_weightTonnes', field: 'weightTonnes', text: 'Weight (tonne)', autoWidth: 1, ...getNumberColumnGenericConfig() },
        { id:'job_volume', field: 'volume', text: 'Volume', autoWidth: 1, ...getNumberColumnGenericConfig() },
        { id:'job_freightPayerName', field: 'freightPayerName', text: 'Freight Payer', autoWidth: 1 },
        { id:'job_division', field: 'division', text: 'Division', autoWidth: 1 },
        {
          id:'job_requiredCompletionDate', field: 'requiredCompletionDate', text: 'Required Completion Date', autoWidth: 1, type: 'date', format: 'DD/MM/YYYY', excelFormat: 'dd/mm/yyyy',
          renderer: ({ record }: any) => {
            return record.requiredCompletionDate && record.requiredCompletionDate.toString() !== 'Invalid Date' ? dayjs(record.requiredCompletionDate).format('DD/MM/YYYY') : '';
          }
        },
        {
          id:'job_jobCompletedDateTime', field: 'jobCompletedDateTime', text: 'Completed Date & Time', autoWidth: 1, type: 'date', format: 'DD/MM/YYYY HH:mm', excelFormat: 'dd/mm/yyyy hh:mm',
          renderer: ({ record }: any) => {
            return record.jobCompletedDateTime && record.jobCompletedDateTime.toString() !== 'Invalid Date' ? dayjs(new Date(record.jobCompletedDateTime)).format('DD/MM/YYYY HH:mm') : '';
          }
        },
        { id:'job_priority', field: 'priority', text: 'Priority', autoWidth: 1 },
        { id:'job_jobReference', field: 'jobReference', text: 'Job Reference', autoWidth: 1 },
        { id:'job_jobDescription', field: 'jobDescription', text: 'Job Description', autoWidth: 1 },
        { id:'job_productCategory', field: 'productCategory', text: 'Product Category', autoWidth: 1 },
        { id:'job_isOnSite', field: 'isOnSite', text: 'Is On-site?', autoWidth: 1, renderer: ({ record }: any) => record.isOnSite ? 'Yes' : 'No' },
        { id:'job_fromAddress', field: 'fromAddress', text: 'From Address', autoWidth: 1 },
        { id:'job_fromRegion', field: 'fromRegion', text: 'From Region', autoWidth: 1 },
        { id:'job_fromLocality', field: 'fromLocality', text: 'From Locality', autoWidth: 1 },
        { id:'job_fromSubLocality', field: 'fromSubLocality', text: 'From Sub Locality', autoWidth: 1 },
        { id:'job_toAddress', field: 'toAddress', text: 'To Address', autoWidth: 1 },
        { id:'job_toRegion', field: 'toRegion', text: 'To Region', autoWidth: 1 },
        { id:'job_toLocality', field: 'toLocality', text: 'To Locality', autoWidth: 1 },
        { id:'job_toSubLocality', field: 'toSubLocality', text: 'To Sub Locality', autoWidth: 1 },
        { id:'job_jobType', field: 'jobType', text: 'Job Type', autoWidth: 1 },
        { id:'job_jobNumber', field: 'jobNumber', text: 'Job Number', autoWidth: 1 },
        { id:'job_lsp', field: 'lsp', text: 'LSP', autoWidth: 1 },
        { id:'job_dsp', field: 'dsp', text: 'DSP', autoWidth: 1 },
        { id:'job_plannedStartTime', field: 'plannedStartTime', text: 'Planned Start Time', autoWidth: 1, type: 'date', format: 'DD/MM/YYYY HH:mm', excelFormat: 'dd/mm/yyyy hh:mm' },
        { id:'job_truck', field: 'truck', text: 'Truck', autoWidth: 1 },
        { id:'job_equipment', field: 'equipment', text: 'Equipment', autoWidth: 1 },
        { id:'job_totalDispatchUnits', field: 'totalDispatchUnits', text: 'Total Dispatch Units', autoWidth: 1, ...getNumberColumnGenericConfig() },
        { id:'job_parentName', field: 'parentName', text: 'Parent Name', autoWidth: 1 },
        { id:'job_executionDate', field: 'executionDate', text: 'Execution Date', autoWidth: 1, type: 'date', format: 'DD/MM/YYYY', excelFormat: 'dd/mm/yyyy' },
        { id:'job_jobShift', field: 'jobShift', text: 'Job Shift', autoWidth: 1 },
      ];
      const excelFormats = new Map(columns.map(column => [column.text, column.excelFormat]));

      this.gridProps = {
        columns: [
          {
            id: 'action',
            width: '10em',
            text: 'Action',
            field: 'action',
            hidden: false,
            align: 'center',
            type: 'widget',
            hideable: false,
            draggable: false,
            htmlEncode: false,
            filterable: false,
            headerRenderer: ({ record }: any) => {
              return `<label>Action</label><br><button class="btn-xs btn btn-primary clearfilter">Clear Filters</button> `;
            },
            widgets: [
              {
                type: 'button',
                text: 'Options',
                menuIcon: undefined,

                onAction: ({ source: btn }: any) => {
                  let menuItems: Partial<MenuItemConfig>[] = [];
                  menuItems.push({ text: 'View Job', });

                  if (btn.cellInfo.record.status === "Completed") {
                    menuItems.push({ text: 'Print POD', });
                  }

                  if (btn.cellInfo.record.data.isEditable) {
                    menuItems.push({ text: 'Edit Order' });
                  }

                  menuItems.push({ text: 'Print Jobsheet', });

                  if (menuItems.length > 0) {
                    const menu = (btn.menu || (btn.menu = new Menu({
                      forElement: btn.element,
                      items: menuItems,
                      onItem: ({ item }) => {
                        if (item.text === 'View Job') {
                          this.openJobEyeView(btn.cellInfo.record.jobId);
                        }
                        if (item.text === 'Print POD') {
                          this.printJobPodPdf(btn.cellInfo.record.jobId);
                        }
                        if (item.text === 'Edit Order') {
                          this.router.navigate([`app/jobbing/${btn.cellInfo.record.jobId}/edit`]);
                        }
                        if (item.text === 'Print Jobsheet') {
                          this.printJobSheetPdf(btn.cellInfo.record.jobId);
                        }
                      }
                    }))) as Menu;
                    menu.show();
                  }
                }
              }
            ],
          },
          {
            id: "job_open_pod",
            text: 'Open POD',
            field: 'openPod',
            editor: false,
            editable: false,
            hidden: false,
            align: 'center',
            width: '10em',
            flex: 1,
            htmlEncode: false,
            renderer: ({ value, isExport, record }: { value: string; isExport: boolean; record: any }) => {
                if (record && record.status === 'Completed') {
                    if (isExport) {
                        return 'Yes';
                    } else {
                        return `<button type="button" class="btn btn-primary w-100">Open POD</button>`;
                    }
                } else {
                    if (isExport) {
                        return 'No';
                    } else {
                        return ``;
                    }
                }
            }
        },
          ...columns
        ],
        minHeight: '85vh',
        rowCopyPasteFeature: false,
        cellMenuFeature: false,
        filterBarFeature: true,
        cellEditFeature: false,
        excelExporterFeature: {
          zipcelx: ({ filename, rows, columns: exportedColumns }: { filename: string, rows: { value: string | number | Date | null | undefined }[][], columns: { field: string, value: string }[] }) => {
            const data = rows.map(row => row.map(({ value }, index): Cell => {
              if (value === null || value === undefined || value === '') {
                return null;
              }
              const format = excelFormats.get(exportedColumns[index].value);
              if (value instanceof Date) {
                return Number.isNaN(value.getTime()) ? null : { value, type: Date, format: format as string };
              }
              if (typeof value === 'number') {
                return { value, type: Number, format: typeof format === 'function' ? format(value) : format };
              }
              return { value, type: String };
            }));
            return writeXlsxFile([
              exportedColumns.map(column => ({ value: column.value, type: String, fontWeight: 'bold' } as Cell)),
              ...data
            ]).toFile(`${filename}.xlsx`);
          }
        },
        store: {
          listeners: {
            filter: (event: any) => {
              this.saveGridState(event);
              this.updateTotalItemsFromStore();
            },
            sort: (event: any) => this.saveGridState(event),
            group: (event: any) => this.saveGridState(event),
            change: (event: any) => {
              this.saveGridState(event);
              this.updateTotalItemsFromStore();
            },
          }
        },
        listeners: {
          columnDrop: (event: any) => this.saveGridState(event),
          columnResize: (event: any) => this.saveGridState(event),
          hide: (event: any) => this.saveGridState(event),
          catchAll: (event: any) => {
            // the below event catches the column hide event
            if (event.type === 'statechange') {
              this.saveGridState(event);
            }
          },
          headerClick: (event: any) => this.headerClick(event),
        }
      };
    });
    this.store$.dispatch(loadGridState({ bryntumComponentTypeId: 27 }));
    this.store$.select(selectGridStateById({ bryntumComponentTypeId: 27 })).subscribe((gridState) => {
      let incomingColumns: any = null;

      if (gridState?.state !== null
        && gridState?.state.Columns !== undefined
        && gridState?.state.Columns !== null
        && gridState?.state.Columns !== '[]') {
        incomingColumns = JSON.parse(gridState?.state.Columns);
      } else {
        this.gridState = this.gridProps;
        this.isInitializing = false;
        return;
      }

      this.gridState = {
        columns: incomingColumns,
        rowHeight: JSON.parse(gridState!.state.RowHeight),
        scroll: JSON.parse(gridState!.state.Scroll),
        selectedRecords: JSON.parse(gridState!.state.SelectedRecords),
        store: JSON.parse(gridState!.state.Store),
        style: JSON.parse(gridState!.state.Style),
        subGrids: JSON.parse(gridState!.state.SubGrids),
      };

      this.isInitializing = false;
    });
  }

  openJobEyeView(jobId: number) {
      const modalRef = this.modalService.open(JobbingJobEyeViewModalComponent, { size: 'xl' });
      modalRef.componentInstance.jobId = jobId;
  }

  onCellClick({ record, column }: { record: any, column: ColumnConfig }) {
      if (column.id === 'job_open_pod' && record.status === 'Completed') {
        this.openJobbingJobPreviewPodModal(record.jobId);
      }
    }

  headerClick(event: any) {
    if (event && event.domEvent && event.domEvent.target && event.domEvent.target.classList.contains('clearfilter')) {
      if (this.jobbingGrid && this.jobbingGrid.instance) {
        (this.jobbingGrid.instance.store as any).clearFilters();
      }
    }
  }

  toggleAllGroupings(action: 'expand' | 'collapse') {
    if (this.jobbingGrid?.instance) {
      if (action === 'collapse') {
        this.jobbingGrid.instance.collapseAll();
      }

      if (action === 'expand') {
        this.jobbingGrid.instance.expandAll();
      }
    }
  }

  exportToCsv() {
    if (!this.jobbingGrid || !this.jobbingGrid.instance) {
      return;
    }

    const grid = this.jobbingGrid.instance;
    const columns = (grid.columns as ColumnStore).visibleColumns.filter(column =>
      column.get('exportable') !== false && column.field && column.field !== 'action');
    const date = dayjs().format('YYYY-MM-DD-HH-mm');
    const exportConfig = {
      filename: `trifecta-jobbing-${date}`,
      dateFormat: '',
      exporterConfig: { columns }
    };
    return grid.features.excelExporter.export(exportConfig);
  }

  createNewJob() {
    this.router.navigate([`app/jobbing/create`]);
  }

  saveGridState(event: any) {
    if (this.lastGridSave.isBefore(dayjs().subtract(1, 'seconds'))) {
      if (!this.isInitializing && this.jobbingGrid?.instance) {

        const allColumns = this.jobbingGrid.instance?.state?.columns?.map((column: any) => {
          const initialColumn: any = (this.gridProps!.columns as GridColumnConfig[]).find((c: any) => c.field === column.field);
          return {
            ...column,
            type: initialColumn?.type,
            format: initialColumn?.format,
          };
        });

        const stateToSave = {
          ...this.jobbingGrid.instance.state,
          Columns: allColumns,
        };

        this.store$.dispatch(saveGridState({ configuration: stateToSave, bryntumComponentTypeId: 27 }));
        this.lastGridSave = dayjs();
      }
    }
  }


  getFilterState() {
    const result = localStorage.getItem('jobbingViewFilterState');
    if (result) {
      return JSON.parse(result);
    }
  }

  saveFilterState(from: Date, to: Date) {
    localStorage.setItem('jobbingViewFilterState', JSON.stringify({
      from: from,
      to: to
    }));
  }

  getNormalizeDateRange() {
    if (this.dateFilterInput === null) {
      return {
        from: new Date(dayjs().add(1, 'day').subtract(this.defaultDaysToFilter, 'days').format()),
        to: new Date(dayjs().add(1, 'day').format())
      };
    } else {

      const fromDayJs = dayjs(this.dateFilterInput.instance.value[0]).add(1, 'day');
      const toDayJs = dayjs(this.dateFilterInput.instance.value[1]).add(1, 'day');

      return {
        from: new Date(fromDayJs.toString()),
        to: new Date(toDayJs.toString())
      };
    }
  }

  getJobToProductInfomationForBryntumGrid(jobType: string, job: Job, contractUom: number | undefined): JobProductBryntumModel {
    let result: JobProductBryntumModel = {
      jobProductDescriptions: "",
      jobProductCategoryNames: "",
      jobEquipments: "",
      totalVolume: 0,
      totalQuantity: 0,
      totalWeight: 0,
      totalDispatchQuantity: 0
    }

    if (jobType === WorkOrderType[WorkOrderType.Job] || jobType === WorkOrderType[WorkOrderType.Project]) {
      const productlines: JobProductItem[] = job.jobProducts.reduce((accumulator: JobProductItem[], product) => accumulator.concat(product.productItems ? product.productItems : []), []);

      result.jobProductDescriptions = productlines.map(items => items.itemDescription).join(',');
      result.totalVolume = productlines.reduce((acc, items) => acc + items.volume, 0);
      result.totalQuantity = productlines.reduce((acc, items) => acc + items.quantity, 0);
      result.totalWeight = productlines.reduce((acc, items) => acc + items.weight, 0);

    } else {
      result.jobProductDescriptions = job.jobProducts.map(product => {
        return product.productDescription;
      }).join(',');


      if (result) {
        const totalQuantity = job.jobProducts.reduce((acc, items) => acc + items.quantity, 0)
        const totalDispatchQuantity = job.jobProducts.reduce((acc, items) => acc + items.dispatchQuantity, 0)

        switch (contractUom) {
          case BillingUomEnum.Units:
            result.totalQuantity = totalQuantity;
            break;

          case BillingUomEnum.Volume:
            result.totalVolume = totalQuantity;
            result.totalDispatchQuantity = totalDispatchQuantity;
            break;

          case BillingUomEnum.Weight:
            result.totalWeight = totalQuantity;
            result.totalDispatchQuantity = totalDispatchQuantity;
            break;
          default:
            // do nothing
            break;
        }
      }
    }

    result.jobProductCategoryNames = job.jobProducts.map(product => product.productCategory ? product.productCategory.name : '').join(',');
    result.jobEquipments = job.jobEquipments ? job.jobEquipments.map(equipment => equipment.name).join(',') : "";
    return result;
  }

  getServicePromise(servicePromise: JobServicePromise, timeValue: Time | null): string {
    if (servicePromise && servicePromise.servicePromiseId === JobServicePromiseEnum.SpecificTime && timeValue) {
      return this.getFormattedServicePromiseTime(timeValue);
    } else {
      return servicePromise.name;
    }
  }

  getFormattedServicePromiseTime(timeValue: Time): string {
    const currentDate = new Date();
    const [hours, minutes, seconds] = timeValue.toString().split(':');
    currentDate.setHours(+hours);
    currentDate.setMinutes(+minutes);
    currentDate.setSeconds(+seconds);
    return dayjs(new Date(currentDate)).format("HH:mm A");
  }

  private updateTotalItemsFromStore() {
    const gridStore = this.jobbingGrid?.instance?.store as any;
    if (!gridStore) {
      this.totalItems = this.data.length;
      return;
    }

    const allRecords: any[] = gridStore.allRecords as any[];
    if (!allRecords || allRecords.length === 0) {
      this.totalItems = 0;
      return;
    }

    const filterFn = gridStore.filtersFunction;
    const hasActiveFilterFn = typeof filterFn === 'function';

    let count = 0;
    for (let i = 0; i < allRecords.length; i++) {
      const record = allRecords[i];
      if (record && !record.isSpecialRow && (!hasActiveFilterFn || filterFn(record))) {
        count++;
      }
    }

    this.totalItems = count;
  }

  loadJobsList() {
    this.jobbingGrid?.instance.mask('Loading...');
    const dateRange = this.getNormalizeDateRange();
    this.jobbingService.getJobs(dateRange.from, dateRange.to).pipe(take(1)).subscribe({
        next: (jobs) => {
          this.data = [];
          jobs.map((jobModel: any) => {
            const productData = this.getJobToProductInfomationForBryntumGrid(jobModel.jobType, jobModel.job, jobModel.job?.contractUom);
            this.data?.push({
              freightPayerName: jobModel.job?.freightPayer?.name,
              division: jobModel.jobDivision,
              requiredCompletionDate: new Date(jobModel.job?.requiredDate),
              parentCompletedDateTime: new Date(jobModel?.parentCompletedDateTime),
              jobCompletedDateTime: new Date(jobModel?.jobCompletedDateUtc),
              priority: jobModel.priority?.name,
              jobReference: jobModel.job?.jobReference,
              jobDescription: jobModel.job?.jobDescription,
              productCategory: productData.jobProductCategoryNames,
              itemDescription: productData.jobProductDescriptions,
              quantity: productData.totalQuantity,
              weightTonnes: productData.totalWeight,
              volume: productData.totalVolume,
              isOnSite: jobModel.job?.onSite,
              fromName: jobModel.fromJobAddressSelection?.name,
              toName: jobModel.toJobAddressSelection?.name,
              jobType: jobModel.jobType,
              status: jobModel.status?.name,
              jobId: jobModel.job?.jobId,
              jobNumber: jobModel.job?.jobNumber,
              orderNumber: jobModel.job?.orderNumber,
              lsp: this.getServicePromise(jobModel.job?.jobLsp, jobModel.job?.jobLspTime),
              dsp: this.getServicePromise(jobModel.job?.jobDsp, jobModel.job?.jobDspTime),
              truck: jobModel.slot?.truck?.code,
              plannedStartTime: new Date(jobModel.slot?.startDate),
              equipment: productData.jobEquipments,
              totalDispatchUnits: productData.totalDispatchQuantity,
              parentName: jobModel.parentName,
              fromAddress: jobModel.fromJobAddressSelection?.address,
              fromRegion: jobModel.fromJobAddressSelection?.region,
              fromLocality: jobModel.fromJobAddressSelection?.locality,
              fromSubLocality: jobModel.fromJobAddressSelection?.subLocality,
              toAddress: jobModel.toJobAddressSelection?.address,
              toRegion: jobModel.toJobAddressSelection?.region,
              toLocality: jobModel.toJobAddressSelection?.locality,
              toSubLocality: jobModel.toJobAddressSelection?.subLocality,
              isEditable: jobModel.isEditable,
              executionDate: jobModel.executionDateUtc ? DateTimeHelper.utcToLocal(jobModel.executionDateUtc) : null,
              jobShift: jobModel.jobShift,
            });
          });
          this.totalItems = this.data.length;
          queueMicrotask(() => this.updateTotalItemsFromStore());
          if (this.jobbingGrid?.instance && !this.jobbingGrid?.instance.isDestroyed) {
            this.jobbingGrid?.instance.unmask();
          }
        },
        error: (err) => {
          this.jobbingGrid?.instance.unmask();
          if (err.status === 401) {
            this.toasterService.showDanger("Refresh token failed. Please log out and log back in.");
          } else {
            this.toasterService.showDanger("Failed to load jobs!", true);
          }
        }
      });
  }

  printJobPodPdf(jobId: number) {
    this.proofOfDeliveryPdfService.printJobPodPdf(jobId).subscribe({
      next: (response) => {
        if (response) {
          this.blobStorageService.downloadFile(response.fileBytes, response.fileName);
        }
      },
      error: (response: HttpErrorResponse) => {
        console.error(response.message, 'An error occurred:');
      }
    });
  }

  printJobSheetPdf(jobId: number) {
    this.proofOfDeliveryPdfService.printJobSheetPdf(jobId).subscribe({
      next: (response) => {
        if (response) {
          this.blobStorageService.downloadFile(response.fileBytes, response.fileName);
        }
      },
      error: (response: HttpErrorResponse) => {
        console.error(response.message, 'An error occurred:');
      }
    });
  }

  openJobbingJobPreviewPodModal(jobId: number) {
      const modalRef = this.modalService.open(JobbingJobPreviewPodModalComponent, { size: 'xl' });
      modalRef.componentInstance.jobId = jobId;
    }
}
